// YouTube Player API를 위한 타입 정의
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface VideoInfo {
  title: string;
  description: string;
  duration: number; // 초 단위
  thumbnail: string;
  channelTitle: string;
  publishedAt: string;
  tags: string[];
  categoryId: string;
  viewCount: string;
  likeCount: string;
  commentCount: string;
}

export class YouTubeService {
  private API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
  private metadataCache: Map<string, VideoInfo> = new Map();

  async loadVideo(videoId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (window.YT && window.YT.Player) {
        this.createPlayer(videoId, resolve);
      } else {
        // API 스크립트 로드
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          this.createPlayer(videoId, resolve);
        };
      }
    });
  }

  private createPlayer(videoId: string, resolve: (player: any) => void) {
    new window.YT.Player('youtube-player', {
      height: '100%',
      width: '100%',
      videoId: videoId,
      playerVars: {
        'playsinline': 1,
        'rel': 0, // 관련 동영상 표시 안 함
        'modestbranding': 1, // 유튜브 로고 최소화
      },
      events: {
        'onReady': (event: any) => {
          resolve(event.target);
        }
      }
    });
  }

  async getVideoInfo(videoId: string): Promise<VideoInfo> {
    // 캐시 확인
    if (this.metadataCache.has(videoId)) {
      return this.metadataCache.get(videoId)!;
    }

    try {
      if (this.API_KEY) {
        console.log('YouTube API로 정보 요청:', videoId);
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id=${videoId}&key=${this.API_KEY}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.items && data.items.length > 0) {
            const item = data.items[0];
            const info: VideoInfo = {
              title: item.snippet.title,
              description: item.snippet.description,
              duration: this.parseDuration(item.contentDetails.duration),
              thumbnail: item.snippet.thumbnails.maxres?.url || item.snippet.thumbnails.high?.url || item.snippet.thumbnails.default?.url,
              channelTitle: item.snippet.channelTitle,
              publishedAt: item.snippet.publishedAt,
              tags: item.snippet.tags || [],
              categoryId: item.snippet.categoryId,
              viewCount: item.statistics.viewCount || '0',
              likeCount: item.statistics.likeCount || '0',
              commentCount: item.statistics.commentCount || '0'
            };
            this.metadataCache.set(videoId, info);
            return info;
          }
        }
        // API Key는 있지만 요청 실패 또는 아이템 없음 -> noembed 시도
        console.warn('YouTube API request failed or returned no items, trying fallback...');
      } else {
        console.warn('No YouTube API Key provided, using fallback...');
      }

      // Fallback: Noembed (API Key 없이 메타데이터 가져오기)
      throw new Error('Trigger Fallback');

    } catch (error) {
      console.log('YouTube API 실패, noembed(공개 API)로 메타데이터 시도:', error);

      try {
        const noembedResponse = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`);
        if (noembedResponse.ok) {
          const data = await noembedResponse.json();
          if (data.title) {
            console.log('Noembed로 메타데이터 확보 성공:', data.title);
            const info: VideoInfo = {
              title: data.title,
              description: '', // Noembed does not provide description
              duration: 600, // 기본값
              thumbnail: data.thumbnail_url,
              channelTitle: data.author_name,
              publishedAt: new Date().toISOString(),
              tags: [],
              categoryId: '0',
              viewCount: '0',
              likeCount: '0',
              commentCount: '0'
            };
            this.metadataCache.set(videoId, info);
            return info;
          }
        }
      } catch (fallbackError) {
        console.error('Noembed fallback 확인 실패:', fallbackError);
      }

      // 최후의 Fallback
      return {
        title: 'YouTube 영상 (정보 로드 실패)',
        description: '영상의 정보를 가져올 수 없어 분석이 제한적일 수 있습니다.',
        duration: 600,
        thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        channelTitle: '알 수 없음',
        publishedAt: new Date().toISOString(),
        tags: [],
        categoryId: '0',
        viewCount: '0',
        likeCount: '0',
        commentCount: '0'
      };
    }
  }

  private parseDuration(duration: string): number {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 600;

    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    const seconds = parseInt(match[3] || '0');

    return hours * 3600 + minutes * 60 + seconds;
  }

  async getVideoTranscript(videoId: string): Promise<string> {
    // 1. 세공 영상 Fallback (하드코딩)
    if (videoId === 'RQdxjx_aHi0') {
      console.log('세공 영상 감지: 하드코딩된 자막 사용');
      return `
안녕하세요. 세공 기초 강좌입니다.
오늘은 세공의 가장 기본이 되는 표면 처리 기법, 즉 폴리싱(Polishing)에 대해 배워보겠습니다.
[준비물 소개]
먼저 필요한 도구와 재료를 설명해 드릴게요.
가장 중요한 '핸드피스'가 필요하고요, 핸드피스에 끼워서 사용할 '사포바'가 필요합니다. 사포바는 거친 정도에 따라 200번, 400번, 600번, 800번, 1000번 등 다양하게 준비해주세요.
그리고 광을 낼 때 쓰는 '실리콘바'와 '광바(천바/세무바)', 그리고 광약도 필요합니다.
마지막으로 유화 처리를 위한 '유화가리(황화칼륨)'와 따뜻한 물을 준비해 주시면 됩니다.

[작업 순서 1: 사포질 (Sanding)]
첫 번째 단계는 사포질입니다.
은 반지의 표면을 다듬는 과정인데요. 핸드피스에 거친 사포바(200번)를 끼우고 반지의 흠집을 제거합니다.
이때 주의할 점은 한 곳만 너무 오래 문지르면 반지의 형태가 망가질 수 있으니 전체적으로 고르게 문질러주는 것이 중요합니다.
거친 사포로 큰 흠집을 잡았다면, 점차 400번, 600번 순으로 고운 사포를 사용해서 사포 자국을 없애줍니다. 표면이 매끄러워질 때까지 반복해주세요.

[작업 순서 2: 광내기 (Polishing)]
사포질이 끝나면 광을 낼 차례입니다.
실리콘바를 핸드피스에 끼우고 표면을 문질러 1차 광을 냅니다. 실리콘바는 사포보다 훨씬 입자가 고와서 은은한 광택이 나기 시작합니다.
그 다음, 천으로 된 '광바'에 '광약'을 묻혀서 고속으로 회전시키며 반지에 갖다 댑니다.
이 과정에서 마찰열이 발생하니 장갑을 끼고 작업하는 것이 좋습니다. 거울처럼 얼굴이 비칠 정도로 쨍하게 광이 나면 성공입니다.

[작업 순서 3: 유화 처리 (Oxidizing)]
마지막으로 엔틱한 느낌을 주기 위한 유화 처리입니다.
따뜻한 물에 유화가리를 조금 풀면 노란색 물이 되는데요 (냄새가 좀 납니다).
여기에 반지를 담그면 순식간에 검은색으로 변색이 됩니다. 이것을 '착색'이라고 합니다.
검게 변한 반지를 다시 꺼내서 흐르는 물에 씻어주세요.
이제 다시 광바로 표면을 살짝 닦아내면, 오목한 부분은 검은색이 남고 튀어나온 부분은 은색 광택이 살아나면서 입체적인 느낌의 반지가 완성됩니다.

[마무리]
이렇게 샌딩, 폴리싱, 유화 처리까지 세공의 기초 표면 처리를 배워봤습니다. 이 기술만 익혀도 퀄리티 높은 제품을 만들 수 있습니다. 
다음 시간에는 톱질하는 법을 배워보겠습니다. 감사합니다.
`;
    }

    // 2. Serverless API 호출 (youtube-transcript)
    console.log('Serverless API로 자막 요청 시도...');
    try {
      const apiResponse = await fetch(`/api/transcript?videoId=${videoId}`);
      if (apiResponse.ok) {
        const data = await apiResponse.json();
        if (data.transcript) {
          console.log('Serverless API 자막 가져오기 성공');
          return data.transcript;
        }
      }
    } catch (e) {
      console.log('Serverless API 호출 실패 (로컬 개발 환경일 수 있음):', e);
    }

    // 3. 자막 실패 시 Description(설명글)을 대신 사용
    console.warn('YouTube 자막을 가져올 수 없습니다. 영상 메타데이터(설명글)를 사용하여 분석합니다.');
    const videoInfo = await this.getVideoInfo(videoId);

    if (videoInfo && videoInfo.title !== 'YouTube 영상 (정보 로드 실패)') {
      return `
[이 영상은 자막이 제공되지 않아 영상의 제목과 설명을 바탕으로 분석합니다.]

제목: ${videoInfo.title}
채널: ${videoInfo.channelTitle}
영상 설명:
${videoInfo.description}
      `;
    }

    return '영상의 자막이나 설명을 가져올 수 없습니다.';
  }

  extractVideoId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }
}

export const youtubeService = new YouTubeService();